/**
 * MyTestResult Component
 *
 * Description: This component fetches and displays the test results for a customer. It retrieves the user data, product data, and biomarker data based on a provided test ID, and renders the results using the DisplayResult component.
 *
 * File Name: MyTestResult.tsx
 * Date: 19-08-2024
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "src/Components/Customer/PageHeader";
import SplashScreen from "src/Components/SplashScreen";
import BackIcon from "src/assets/images/back-icon.png";
import DisplayResult from "src/Components/GlobalComponents/DisplayResult";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import {
  IResultBiomarker,
  IResultDetails,
  IResultProduct,
  IResultUser,
} from "src/types/GlobalInterfaces";
import { getIndicators, updateIndicators } from "src/Store/redDots";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";
import { useSelector } from "react-redux";

const MyTestResult: React.FC = () => {
  const { id = "" } = useParams(); // Extract user ID from URL parameters
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoader, setIsLoader] = useState<boolean>(false); // State to manage loading status

  const [userData, setUserData] = useState<IResultUser | null>(null);
  const [productData, setProductData] = useState<IResultProduct | null>(null);
  const [biomarkerData, setBiomarkerData] = useState<IResultBiomarker[]>([]);
  const { redDots } = useSelector((store: RootState) => store.redDots);

  // Component to display a back button in the page header
  const BackButton: React.FC = () => {
    return (
      <button className="right-bar-icon" onClick={() => navigate(-1)}>
        <img src={BackIcon} alt="back-icon" />
      </button>
    );
  }; // display back icon in page header

  // API_CALL: Function to fetch test result details
  const fetchResult = async (_id: string) => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IResultDetails>({
      path: `/test/single-details/${_id}`,
    });
    if (res) {
      const { data } = res;
      // Update states with fetched data
      setUserData(data.userData[0]);
      setProductData(data.productData[0]);
      setBiomarkerData(data.biomarkerData);
      if(redDots.resultNotified === 1) {
        dispatch(updateIndicators({ customerResult: 0 }));
      }
    } else {
      // Display error toast notification
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  // Effect to fetch result when the component mounts or when the ID changes
  useEffect(() => {
    if (id) {
      fetchResult(id);
    }
  }, [id]);

  return (
    <>
      {isLoader && <SplashScreen />}
      {/* <PageHeader
                title="Results"
                isButton={true}
                ButtonComponent={BackButton}
            /> */}
      <div className="w-full h-full p-2 2xl:p-3 bg-banner-white-image">
        <div className="view-area-width mx-auto">
          {userData && productData && (
            <DisplayResult
              userData={userData}
              productData={productData}
              biomarkerData={biomarkerData}
              parentBoxClassName="inner-max-width mb-0"
              isCustomerSide={true}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MyTestResult;
